/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useIntl } from 'umi';
import { List, Popover, DropdownBox, Bubble } from 'tea-component';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { isRateColumn, isRateSortSupportedPath } from './_utils';

const versionDailyPath = '/overview/versionDaily';
const SortPopover = (props: any) => {
  const { value, col, sorting: propsSorting, getRealSort , handleTableCopy, copyTitleList = ['current', 'copy date', 'copy current and date'], placement = 'bottom-start', className, style, useNewActiveLogic = false, showCopyOptions = true } = props;
  const sorting = getRealSort() || propsSorting;
  // 当前已有业务分为时间列和日期列，
  const isHaveDate = copyTitleList?.includes('copy date');
  const isHaveTime = copyTitleList?.includes('copy time');
  const isHaveDateOrTime = isHaveDate || isHaveTime;
  const isHaveCurrent = copyTitleList?.includes('current');
  const isHaveDateAndCurrent = copyTitleList?.includes('copy current and date');
  const isHaveTimeAndCurrent = copyTitleList?.includes('copy current and time');
  const isHaveDateTimeAndCurrent = copyTitleList?.includes('copy current and Datetime');
  const { formatMessage } = useIntl();
  // 升序 asc 降序 desc 默认 default
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState('default');

  const explain = col?.explain ? col?.explain?.replaceAll('\\n', '<br/>') : null;

  // 临时处理versionDaily
  const location = useLocation();
  const pathName = location.pathname;
  const isVersionDaily = pathName?.includes(versionDailyPath);

  const options = [
    {
      value: 'asc',
      text: formatMessage({ id: 'common_asc' }),
      icon: 'arrow-other-up'
    },
    {
      value: 'desc',
      text: formatMessage({ id: 'common_des' }),
      icon: 'arrow-other-down'
    },
    ...(isRateColumn(col) && isRateSortSupportedPath(pathName) ? [
      {
        value: 'asc_by_rate',
        text: formatMessage({ id: 'common_asc_by_rate' }),
        icon: 'arrow-other-up'
      },
      {
        value: 'desc_by_rate',
        text: formatMessage({ id: 'common_des_by_rate' }),
        icon: 'arrow-other-down'
      },
    ] : []),
    {
      value: 'default',
      text: formatMessage({ id: 'common_default_sort' }),
      icon: 'sort-icon'
    }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSort = (order: any) => {
    setVisible(false);
    if (order === 'default') {
      sorting?.onSortChange && sorting.onSortChange([], []);
      setState('default');
    } else {
      sorting?.onSortChange && sorting.onSortChange([{ by: col?.key, order }], []);
      setState(order);
    }
  };

  const handleCopy = (type: string) => {
    handleTableCopy(type, col?.copyKey || col?.key);
    setVisible(false);
  };

  useEffect(() => {
    if (visible) {
      if (useNewActiveLogic) {
        if (visible) {
          if (sorting?.defaultSorts?.length) {
            const currentSort = sorting.defaultSorts.find((sort: any) => sort.by === col?.key);
            if (currentSort && currentSort.order !== 'default') {
              setState(currentSort.order);
            } else {
              setState('default');
            }
          } else {
            setState('default');
          }
          return;
        }
      }

      // 初始化判断是否为选中的排序列，不是重置为default
      const isActive = document.getElementsByClassName(`header-click-${col?.key}`)?.[0]?.children?.[0]?.children?.[0];
      if (!isActive?.classList?.contains('is-active')) {
        setState('default');
      } else if (sorting?.defaultSorts?.length) {
        // 解决指标排序后，设置不显示指标，再设置回显示指标，该组件排序状态消失问题
        // 从sorting中依赖已经排序过的表头，重新设置表头排序状态
        sorting?.defaultSorts?.forEach((sort: any) => {
          const { by, order } = sort || {};
          if (by === col?.key) setState(order);
        });
      }
    }
  }, [col?.key, sorting?.defaultSorts, visible, useNewActiveLogic]);
  
  const renderDate = () => <List.Item onClick={() => handleCopy('date')}>{formatMessage({ id: 'common_copy_date_column' })}</List.Item>;

  const renderTime = () => <List.Item onClick={() => handleCopy('date')}>{formatMessage({ id: 'common_copy_time_column' })}</List.Item>;

  // 针对后端返回 Date :  YYYY-MM-DD HH:MM:SS
  const renderDateTime = () => <List.Item onClick={() => handleCopy('time')}>{formatMessage({ id: 'common_copy_time_column' })}</List.Item>;

  const renderConstrastDate = () => {
    if (!isHaveDateOrTime) return null;
    return <List.Item onClick={() => handleCopy('contrast_date')}>{formatMessage({ id: 'common_copy_contrast_date_column' })}</List.Item>;
  };

  const renderCurrent = () => <List.Item onClick={() => handleCopy('current')}>{formatMessage({ id: 'common_copy_this_column' })}</List.Item>;

  const renderDateAndCurrent = () => <List.Item onClick={() => handleCopy('currentAndDate')}>
    {formatMessage({ id: 'common_copy_this_column_and_date' })}
  </List.Item>;

  const renderTimeAndCurrent = () => <List.Item onClick={() => handleCopy('currentAndDate')}>
    {formatMessage({ id: 'common_copy_this_column_and_time' })}
  </List.Item>;

  const renderDateTimeAndCurrent = () => <List.Item onClick={() => handleCopy('currentAndTime')}>
    {formatMessage({ id: 'common_copy_this_column_and_time' })}
  </List.Item>;
  const handleItemClick = useCallback((value: any) => {
    getSort(value);
  }, [getSort]);

  return (
    <Popover
      visible={visible}
      closeOnScroll={true}
      updateOnDimensionChange={false}
      onVisibleChange={(visible) => {
        if (col?.visible === false) return;
        setVisible(visible);
      }}
      placement={placement}
      trigger="click"
      overlay={
        <DropdownBox>
          <div className='dbd-performance-sort-overlay'>
            <List type="option">
              {options?.map((item: any, i) => <List.Item
                key={i + item?.value}
                className={state === item?.value ? 'is-selected' : ''}
                onClick={(e) => {
                  handleItemClick(item?.value);
                  e.stopPropagation();
                }}
                style={{ verticalAlign: "middle" }}>
                {
                  item?.icon && (
                    <SvgIcon
                      type={item.icon}
                      style={{ cursor: "pointer", marginRight: "5px" }}
                    />
                  )
                }
                {item?.text}
              </List.Item>)}
            </List>
            {showCopyOptions &&
              <>
                <hr style={{ margin: '5px -6px' }} />
                <List type="option">
                  {
                    !isVersionDaily &&
                    (
                      col.key === 'date' ?
                        <>
                          {isHaveDate && renderDate()}
                          {isHaveTime && renderTime()}
                        </>
                        : col.key === 'contrast_date' ?
                          renderConstrastDate()
                          : col.key === 'time' ?
                            <>
                              {isHaveTime && renderDateTime()}
                            </>
                            :
                            <>
                              {isHaveCurrent && renderCurrent()}
                              {isHaveDateAndCurrent && renderDateAndCurrent()}
                              {isHaveTimeAndCurrent && renderTimeAndCurrent()}
                              {isHaveDateTimeAndCurrent && renderDateTimeAndCurrent()}
                            </>
                    )
                  }
                </List>
              </>
            }
          </div>
        </DropdownBox>
      }
    >
      <Bubble
        dark
        arrowPointAtCenter
        content={explain && <div dangerouslySetInnerHTML={{ __html: explain }} />}
      >
        {/* 用于扩充点击区域 */}
        <span
          style={{ cursor: explain ? 'help' : 'pointer' }} className='dbd-table-header-click'>
        </span>
        <span
          className={className}
          style={{ cursor: explain ? 'help' : 'pointer', ...style }}>
          {value}
        </span>
      </Bubble>
    </Popover>
  );
};

export default SortPopover;